import React, { useRef, useEffect, useState, useMemo } from 'react';
import styles from './Chart.module.css';
import { useSelector } from 'react-redux';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import QuestionnaireGraphScore from './components/QuestionnaireGraphScore';
const scoreField = (questionnaireId) => `score_${questionnaireId}`;
const rowPayloadField = (questionnaireId) => `__row_${questionnaireId}`;

const QuestionnaireChartTooltip = ({ active, label, payload, questionnaireIds, lineColors }) => {
  if (!active || !payload?.length) return null;
  const dataPoint = payload[0].payload;
  return (
    <div
      style={{
        fontSize: '0.625rem',
        fontFamily: 'Lato, sans-serif',
        background: '#fff',
        border: '1px solid #ccc',
        padding: '8px 10px',
      }}
    >
      <div style={{ marginBottom: 4 }}>{label}</div>
      {questionnaireIds.map((q, index) => {
        const val = dataPoint[scoreField(q.questionnaireId)];
        if (val === undefined || val === null) return null;
        return (
          <div key={q.questionnaireId} style={{ color: lineColors[index] || '#000' }}>
            {q.name}: {val}
          </div>
        );
      })}
    </div>
  );
};

const QuestionnaireScoreCardView = ({data, opacity, setOpacity}) => {
  const chartContainerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [chartHeight, setChartHeight] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalData, setModalData] = useState(null);

  const handleShowModal = () => {
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleLegendClick = (e) => {
    const qName = e.value;
    setOpacity((op) => ({ ...op, [qName]: op[qName] === 1 ? 0 : 1 }));
  };

  useEffect(() => {
    const updateDimensions = () => {
      if (chartContainerRef.current) {
        const containerWidth = chartContainerRef.current.offsetWidth;
        const width = containerWidth - 50;
        setChartWidth(width);
      }
      if (chartContainerRef.current) {
        const containerHeight = chartContainerRef.current.offsetHeight;
        const height = containerHeight - 50;
        setChartHeight(height);
      }
    };
    updateDimensions();

    window.addEventListener('resize', updateDimensions);

    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  const questionnaireIds = useSelector((state)=>state.patient.questionnaireIds);

  const chartData = useMemo(() => {
    if (!data?.length || !questionnaireIds?.length) return [];
    const byQDate = new Map();
    const sorted = [...data].sort(
      (a, b) => new Date(a.sortDate) - new Date(b.sortDate)
    );
    for (const row of sorted) {
      const qid =
        row.questionnaireId ??
        questionnaireIds.find((q) => q.name === row.name)?.questionnaireId;
      if (qid == null) continue;
      const dateKey = row.qDate;
      if (!byQDate.has(dateKey)) {
        byQDate.set(dateKey, { qDate: dateKey, sortDate: row.sortDate });
      }
      const point = byQDate.get(dateKey);
      if (new Date(row.sortDate) < new Date(point.sortDate)) {
        point.sortDate = row.sortDate;
      }
      point[scoreField(qid)] = row.score;
      point[rowPayloadField(qid)] = row;
    }
    return Array.from(byQDate.values()).sort(
      (a, b) => new Date(a.sortDate) - new Date(b.sortDate)
    );
  }, [data, questionnaireIds]);

  const handlePointClick = (payload, questionnaireId) => {
    const row =
      questionnaireId != null ? payload[rowPayloadField(questionnaireId)] : null;
    setModalData(row ?? payload);
    handleShowModal();
  };

  const maxYRange = () => {
    return questionnaireIds.reduce((total, item) => Math.max(total, item.totalPossibleScore), 0);
  };

  const yTicks = Array.from(Array(maxYRange() + 1).keys());

  const lineColors = {
    0: '#496CA3',
    1: '#2A7F38',
    2: '#946705',
  };

  return (
    <div className={styles.questionnairePanel} ref={chartContainerRef}>
      <LineChart width={chartWidth} height={chartHeight} data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="qDate" />
        <YAxis domain={[-1, maxYRange()]} ticks={yTicks} />
        <Tooltip
          content={
            <QuestionnaireChartTooltip
              questionnaireIds={questionnaireIds}
              lineColors={lineColors}
            />
          }
        />
        <Legend 
          verticalAlign="top"
          height={50} 
          iconType="square"
          wrapperStyle={{ color: 'red', fontSize: '16px', fontWeight: 'bold' }} 
          payload={questionnaireIds.map((q, index) => ({
            value: q.name,
            type: 'square',
            color: lineColors[index] || '#000000',
          }))}
          onClick={handleLegendClick}
        />
          {questionnaireIds.map((q, index) => (
            <Line
              connectNulls
              type="linear"
              key={q.questionnaireId}
              dataKey={scoreField(q.questionnaireId)}
              stroke={lineColors[index]}
              strokeOpacity={opacity[q.name] || 0}
              strokeWidth={2}
              dot={(props) => {
                const { cx, cy, stroke, value, key, payload } = props;
                const dotColor = value === -1 ? 'red' : lineColors[index];
                if (value !== undefined && value !== null) {
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={7}
                      onClick={(e) => {
                        e.stopPropagation(); 
                        handlePointClick(payload, q.questionnaireId);
                      }}
                      style={{ pointerEvents: 'visible' }}
                      fill={ "white"}
                      stroke={dotColor}
                      strokeOpacity={opacity[q.name] || 0}
                      strokeWidth={2}
                      key={key}
                    />
                  );
                } else {
                  return null;
                }
              }}
              activeDot={(props) => {
                const { cx, cy, stroke, value, key , payload} = props;
                const dotColor = value === -1 ? 'red' : lineColors[index];
                if (value !== undefined && value !== null) {
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={7}
                      onClick={(e) => {
                        e.stopPropagation(); // Ensure the event doesn't bubble up
                        handlePointClick(payload, q.questionnaireId);
                      }}
                      style={{ pointerEvents: 'visible' }}
                      fill={dotColor}
                      stroke={dotColor}
                      strokeOpacity={opacity[q.name] || 0}
                      strokeWidth={2}
                      key={key}
                    />
                );}else{
                  return null;
                }
              }}
            />
          ))}
      </LineChart>
      <QuestionnaireGraphScore 
        isModalVisible={isModalVisible} 
        handleCloseModal={handleCloseModal} 
        modalData={modalData}
      />
    </div>
  );
};

export default QuestionnaireScoreCardView;
